import { NavLink } from 'react-router'
import { MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import {
  ADMIN_ROUTE_CATALOG,
  ADMIN_ICON_MAP,
} from '@/lib/routes/admin-route-catalog'
import type { UserRole } from '@atsready/shared'
import { ROLE_HIERARCHY } from '@atsready/shared'

interface MobileBottomNavProps {
  onMoreClick: () => void
}

export default function MobileBottomNav({ onMoreClick }: MobileBottomNavProps) {
  const { user } = useAuth()

  // Get nav items the user can see, grouped by navGroup
  const allowedRoutes = user
    ? ADMIN_ROUTE_CATALOG.filter(
        (route) =>
          route.includeInNav &&
          ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[route.minRole as UserRole],
      )
    : []

  // Pick first 3 from Core group
  const coreItems = allowedRoutes
    .filter((r) => r.navGroup === 'Core')
    .slice(0, 3)

  // Pick first 1 from Insights group
  const insightsItems = allowedRoutes
    .filter((r) => r.navGroup === 'Insights')
    .slice(0, 1)

  const bottomNavItems = [...coreItems, ...insightsItems]

  return (
    <nav
      aria-label="Mobile navigation"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card md:hidden"
    >
      <div className="flex items-center justify-around">
        {bottomNavItems.map((item) => {
          const Icon = ADMIN_ICON_MAP[item.iconKey]
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              data-touch-target
              className={({ isActive }) =>
                cn(
                  'flex min-h-[44px] min-w-[44px] flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium transition-colors',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              <Icon className="h-5 w-5" />
              <span>{item.navLabel}</span>
            </NavLink>
          )
        })}
        <button
          type="button"
          data-touch-target
          aria-label="More"
          onClick={onMoreClick}
          className="flex min-h-[44px] min-w-[44px] flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <MoreHorizontal className="h-5 w-5" />
          <span>More</span>
        </button>
      </div>
    </nav>
  )
}
